import React from "react";
import Home from "../Home/Home";
import { connect } from "react-redux";
import {
  Button,
  TextField,
  CircularProgress,
  Chip,
  Tooltip,
  MenuItem,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import VideocamIcon from "@material-ui/icons/Videocam";
import HelpIcon from "@material-ui/icons/Help";

import {
  getInboxMessages,
  generateDraft,
  updateDraftBody,
  decideDraft,
  sendDraft,
  startWatch,
  getAvatars,
  createDraftVideo,
  getDraftStats,
} from "../../Redux/Actions/agent";
import { selectInboxMessages, selectAvatars, selectDraftStats } from "../../Redux/Selectors";
import "./style.css";

interface IProps {
  messages: any[];
  avatars: any[];
  stats: any;
  loading: boolean;
  generating: boolean;
  sendingId: string | null;
  getInboxMessages: () => void;
  generateDraft: (inboundMessageId: string, force?: boolean) => void;
  updateDraftBody: (id: string, body: string) => void;
  decideDraft: (id: string, action: string) => void;
  sendDraft: (id: string) => void;
  startWatch: () => void;
  getAvatars: () => void;
  createDraftVideo: (id: string, payload?: any) => void;
  getDraftStats: () => void;
}

const CLASSIFICATION_LABEL: any = {
  interested: { label: "Interested", color: "#2e7d32" },
  needs_followup: { label: "Needs follow-up", color: "#ed6c02" },
  not_interested: { label: "Not interested", color: "#9e9e9e" },
  out_of_office: { label: "Out of office", color: "#9e9e9e" },
  not_a_reply: { label: "Not a reply", color: "#9e9e9e" },
};

class Inbox extends React.Component<IProps> {
  state = {
    selectedId: null as string | null,
    editBody: "" as string,
    editingDraftId: null as string | null,
    videoAvatarId: "",
  };

  componentDidMount() {
    this.props.getInboxMessages();
    this.props.getDraftStats();
    this.props.getAvatars();
  }

  selectMessage = (message: any) => {
    this.setState({
      selectedId: message.id,
      editingDraftId: null,
      editBody: message.draft ? message.draft.body : "",
    });
  };

  startEditing = (draft: any) => {
    this.setState({ editingDraftId: draft.id, editBody: draft.body });
  };

  saveEdit = (draft: any) => {
    this.props.updateDraftBody(draft.id, this.state.editBody);
    this.setState({ editingDraftId: null });
  };

  refresh = () => {
    this.props.getInboxMessages();
    this.props.getDraftStats();
  };

  renderMessageRow = (message: any) => {
    const draft = message.draft;
    const classification = CLASSIFICATION_LABEL[message.classification] || null;
    return (
      <div
        key={message.id}
        className={`inboxRow ${this.state.selectedId === message.id ? "inboxRowActive" : ""}`}
        onClick={() => this.selectMessage(message)}
      >
        <div className="inboxRowTop">
          <span className="inboxFrom">{message.fromName || message.fromEmail}</span>
          <span className="inboxDate">
            {message.receivedAt ? new Date(message.receivedAt).toLocaleDateString() : ""}
          </span>
        </div>
        <div className="inboxSubject">{message.subject || "(no subject)"}</div>
        <div className="inboxSnippet">{message.snippet || message.bodyText}</div>
        <div className="inboxBadges">
          {classification && (
            <span className="inboxBadge" style={{ backgroundColor: classification.color }}>
              {classification.label}
            </span>
          )}
          {draft && (
            <span className={`inboxDraftBadge inboxDraftBadge-${draft.status}`}>
              {draft.status === "sent"
                ? "Sent"
                : draft.status === "skipped"
                ? "Skipped"
                : draft.status === "rejected"
                ? "Rejected"
                : draft.status === "failed"
                ? "Failed"
                : "Draft ready"}
            </span>
          )}
        </div>
      </div>
    );
  };

  renderDraftPanel = (message: any) => {
    const draft = message.draft;
    const { generating, sendingId } = this.props;

    if (!draft) {
      return (
        <div className="inboxDraftEmpty">
          <div className="inboxEmptyText">
            No draft yet for this message. Generate one with your agent.
          </div>
          <Button
            variant="contained"
            className="agentPrimaryButton"
            disabled={generating}
            onClick={() => this.props.generateDraft(message.id)}
          >
            {generating ? "Generating..." : "Generate draft"}
          </Button>
        </div>
      );
    }

    const isEditing = this.state.editingDraftId === draft.id;
    const canEdit = draft.status === "pending" || draft.status === "approved";
    const video = draft.video || {};
    const completedAvatars = (this.props.avatars || []).filter(
      (avatar: any) => avatar.status === "completed"
    );

    return (
      <div className="inboxDraftPanel">
        <div className="inboxDraftMeta">
          <Chip
            size="small"
            label={draft.decision === "reply" ? "Reply" : draft.decision === "escalate" ? "Escalate" : "No reply"}
            color={draft.decision === "reply" ? "primary" : "secondary"}
          />
          {draft.classification && (
            <span className="inboxDraftMetaText">{draft.classification}</span>
          )}
          {draft.confidence !== null && draft.confidence !== undefined && (
            <span className="inboxDraftMetaText">
              confidence {Math.round((draft.confidence || 0) * 100)}%
            </span>
          )}
          {draft.critic && draft.critic.verdict && (
            <span className="inboxDraftMetaText">critic {draft.critic.verdict}</span>
          )}
          {draft.usage && (
            <Tooltip title="Model calls used for this draft">
              <span className="inboxDraftMetaText">{draft.usage.calls || 0} calls</span>
            </Tooltip>
          )}
        </div>

        {draft.decision !== "reply" ? (
          <div className="inboxDraftReason">
            The agent decided not to reply. Reason: {draft.reason || draft.decision}
          </div>
        ) : (
          <>
            {isEditing ? (
              <>
                <TextField
                  value={this.state.editBody}
                  onChange={(event) => this.setState({ editBody: event.target.value })}
                  variant="outlined"
                  multiline
                  rows={10}
                  fullWidth
                />
                <div className="inboxDraftActions">
                  <Button variant="contained" className="agentPrimaryButton" onClick={() => this.saveEdit(draft)}>
                    Save changes
                  </Button>
                  <Button onClick={() => this.setState({ editingDraftId: null })}>Cancel</Button>
                </div>
              </>
            ) : (
              <>
                <div className="inboxDraftBody">{draft.body}</div>
                {canEdit && (
                  <Button size="small" onClick={() => this.startEditing(draft)}>
                    Edit
                  </Button>
                )}
              </>
            )}

            <div className="inboxDraftActions">
              {draft.status === "pending" && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<CheckIcon />}
                    className="agentPrimaryButton"
                    onClick={() => this.props.decideDraft(draft.id, "approve")}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={() => this.props.decideDraft(draft.id, "reject")}
                  >
                    Reject
                  </Button>
                </>
              )}
              {draft.status === "approved" && (
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  className="agentPrimaryButton"
                  disabled={sendingId === draft.id}
                  onClick={() => this.props.sendDraft(draft.id)}
                >
                  {sendingId === draft.id ? "Sending..." : "Send reply"}
                </Button>
              )}
              {(draft.status === "sent" || draft.status === "failed") && (
                <div className="inboxDraftStatus">
                  {draft.status === "sent"
                    ? `Sent${draft.sentAt ? ` on ${new Date(draft.sentAt).toLocaleString()}` : ""}`
                    : `Send failed: ${draft.error || "unknown error"}`}
                </div>
              )}
            </div>

            <div className="inboxVideoSection">
              <div className="inboxSectionTitle">
                Video reply
                <Tooltip
                  title="Turn this draft into a HeyGen avatar video. Review the script first, then generate."
                  arrow
                >
                  <HelpIcon fontSize="small" className="inboxHelpIcon" />
                </Tooltip>
              </div>
              {video.status === "completed" && video.videoUrl ? (
                <div className="inboxVideoReady">
                  <video controls width="100%" src={video.videoUrl} preload="metadata" />
                </div>
              ) : video.status === "processing" ? (
                <div className="inboxVideoStatus">
                  Video is rendering. Refresh this page in a minute.
                  <Button size="small" onClick={this.refresh}>
                    Refresh
                  </Button>
                </div>
              ) : (
                <>
                  <div className="inboxVideoNote">
                    Script: {(video.script || draft.body || "").slice(0, 160)}
                    {(video.script || draft.body || "").length > 160 ? "..." : ""}
                  </div>
                  {completedAvatars.length === 0 ? (
                    <div className="inboxVideoNote">
                      Add and finish an avatar on the Avatars page first.
                    </div>
                  ) : (
                    <div className="inboxVideoControls">
                      <TextField
                        select
                        size="small"
                        variant="outlined"
                        label="Avatar"
                        value={this.state.videoAvatarId}
                        onChange={(event) => this.setState({ videoAvatarId: event.target.value })}
                        className="inboxVideoAvatarSelect"
                      >
                        {completedAvatars.map((avatar: any) => (
                          <MenuItem key={avatar.id} value={avatar.id}>
                            {avatar.name}
                            {avatar.isDefault ? " (default)" : ""}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Button
                        variant="outlined"
                        startIcon={<VideocamIcon />}
                        disabled={generating}
                        onClick={() =>
                          this.props.createDraftVideo(draft.id, {
                            avatarId: this.state.videoAvatarId || undefined,
                          })
                        }
                      >
                        Generate video
                      </Button>
                    </div>
                  )}
                  {video.error && <div className="inboxDraftStatus">Video error: {video.error}</div>}
                </>
              )}
            </div>
          </>
        )}

        {draft.reason && draft.decision === "reply" && (
          <div className="inboxDraftReason">Agent reason: {draft.reason}</div>
        )}
      </div>
    );
  };

  renderSelected = () => {
    const message = this.props.messages.find((entry) => entry.id === this.state.selectedId);
    if (!message) {
      return (
        <div className="inboxEmpty">
          <div className="inboxEmptyText">
            Select a message on the left to review the agent's draft reply.
          </div>
        </div>
      );
    }
    return (
      <div className="inboxDetail">
        <div className="inboxDetailHeader">
          <div className="inboxDetailSubject">{message.subject || "(no subject)"}</div>
          <div className="inboxDetailFrom">
            From {message.fromName || message.fromEmail} &lt;{message.fromEmail}&gt;
          </div>
        </div>
        <div className="inboxOriginBody">{message.bodyText}</div>
        <div className="inboxDetailDivider" />
        {this.renderDraftPanel(message)}
      </div>
    );
  };

  render() {
    const { messages, loading, stats } = this.props;
    return (
      <Home>
        <div className="inboxWrapper">
          <div className="inboxHeader">
            <div>
              <span className="heading">Inbox Replies </span>
              <span className="headingTooltip">
                <Tooltip
                  title="Every email reply the agent has seen. Drafts stay here until you approve and send them."
                  placement="right-start"
                  arrow
                >
                  <HelpIcon />
                </Tooltip>
              </span>
              {stats && (
                <div className="agentSub">
                  {stats.inbound} received · {stats.pending} awaiting review · {stats.sent} sent
                </div>
              )}
            </div>
            <div className="inboxHeaderActions">
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={this.refresh}>
                Refresh
              </Button>
              <Button
                variant="contained"
                className="agentPrimaryButton"
                onClick={this.props.startWatch}
              >
                Enable live sync
              </Button>
            </div>
          </div>

          {loading && messages.length === 0 ? (
            <div className="agentLoading">
              <CircularProgress />
            </div>
          ) : messages.length === 0 ? (
            <div className="inboxEmpty">
              <div className="inboxEmptyText">
                No replies yet. Enable live sync after connecting Gmail, and replies will appear
                here automatically.
              </div>
            </div>
          ) : (
            <div className="inboxLayout">
              <div className="inboxList">{messages.map(this.renderMessageRow)}</div>
              <div className="inboxDetailWrapper">{this.renderSelected()}</div>
            </div>
          )}
        </div>
      </Home>
    );
  }
}

const mapStateToProps = (state: any) => ({
  messages: selectInboxMessages(state),
  avatars: selectAvatars(state),
  stats: selectDraftStats(state),
  loading: state?.agent?.loading,
  generating: state?.agent?.generating,
  sendingId: state?.agent?.sendingId,
});

export default connect(mapStateToProps, {
  getInboxMessages,
  generateDraft,
  updateDraftBody,
  decideDraft,
  sendDraft,
  startWatch,
  getAvatars,
  createDraftVideo,
  getDraftStats,
})(Inbox);
